import React, { useState, useEffect } from 'react';
import { Settings } from 'lucide-react';
import { Encounter } from '../../types/encounter';
import { EncounterSectionTemplate, QuestionType } from '../../types/encounterTemplate';
import { getTemplatesForSection } from '../../utils/encounterTemplateApi';
import { updateEncounter } from '../../utils/encounterApi';
import { useAuth } from '../../contexts/AuthContext';
import TemplateEditorModal from './TemplateEditorModal';

interface PlanSectionProps {
  encounter: Encounter | null;
  onUpdate: () => void;
}

const PlanSection: React.FC<PlanSectionProps> = ({ encounter, onUpdate }) => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<EncounterSectionTemplate[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (encounter && user?.practice_id) {
      fetchTemplates();
    }
  }, [encounter?.id, user?.practice_id]);

  // Sync form data whenever encounter changes - always show fresh data
  useEffect(() => {
    if (encounter) {
      setFormData(encounter.plan_data || {});
    }
  }, [encounter]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const fetchTemplates = async () => {
    if (!user?.practice_id) return;

    setIsLoadingTemplates(true);
    setError(null);

    try {
      const data = await getTemplatesForSection(user.practice_id, 'plan');
      setTemplates(data);
    } catch (err: any) {
      console.error('Failed to fetch templates:', err);
      setError(err.message || 'Failed to load plan questions');
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const handleFieldChange = (questionKey: string, value: any) => {
    const updatedData = { ...formData, [questionKey]: value };
    setFormData(updatedData);

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce auto-save - wait 1 second after user stops typing
    if (encounter) {
      saveTimeoutRef.current = setTimeout(async () => {
        setIsSaving(true);
        const startTime = Date.now();
        
        try {
          await updateEncounter(encounter.pet_id, encounter.id, {
            plan_data: updatedData,
          });
        } catch (err: any) {
          console.error('Failed to save plan data:', err);
          // Don't show error to user for auto-save failures
        }
        
        // Ensure the "Saving..." indicator shows for at least 800ms
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, 800 - elapsedTime);
        
        setTimeout(() => {
          setIsSaving(false);
        }, remainingTime);
      }, 1000); // 1 second debounce
    }
  };

  const renderQuestionInput = (template: EncounterSectionTemplate) => {
    const value = formData[template.question_key];

    switch (template.question_type as QuestionType) {
      case 'text':
        return (
          <textarea
            id={template.question_key}
            value={value || ''}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            required={template.is_required}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 min-h-[60px] resize-y"
            placeholder={`Enter ${template.question_text.toLowerCase()}`}
          />
        );

      case 'yes_no':
        return (
          <div className="flex items-center space-x-4 pt-1">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name={template.question_key}
                value="yes"
                checked={value === 'yes' || value === true}
                onChange={() => handleFieldChange(template.question_key, 'yes')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-1.5 text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name={template.question_key}
                value="no"
                checked={value === 'no' || value === false}
                onChange={() => handleFieldChange(template.question_key, 'no')}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="ml-1.5 text-sm text-gray-700">No</span>
            </label>
          </div>
        );

      case 'select':
        return (
          <select
            id={template.question_key}
            value={value || ''}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            required={template.is_required}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Select an option</option>
            {template.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'multi_select':
        return (
          <div className="space-y-1.5">
            {template.options?.map((option) => (
              <label key={option} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  value={option}
                  checked={Array.isArray(value) && value.includes(option)}
                  onChange={(e) => {
                    const currentValue = Array.isArray(value) ? value : [];
                    const newValue = e.target.checked
                      ? [...currentValue, option]
                      : currentValue.filter((v) => v !== option);
                    handleFieldChange(template.question_key, newValue);
                  }}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-1.5 text-sm text-gray-700">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            id={template.question_key}
            value={value || ''}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            required={template.is_required}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            id={template.question_key}
            value={value || ''}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            required={template.is_required}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder={`Enter ${template.question_text.toLowerCase()}`}
          />
        );

      default:
        return (
          <input
            type="text"
            id={template.question_key}
            value={value || ''}
            onChange={(e) => handleFieldChange(template.question_key, e.target.value)}
            required={template.is_required}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder={`Enter ${template.question_text.toLowerCase()}`}
          />
        );
    }
  };

  if (isLoadingTemplates) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchTemplates}
            className="mt-2 text-red-600 hover:text-red-800 underline text-sm"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 relative">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Plan</h2>
        <button
          onClick={() => setIsEditorOpen(true)}
          className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm transition-colors"
          title="Edit template"
        >
          <Settings className="w-4 h-4" />
          <span className="font-medium">Edit Template</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {templates.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No plan questions configured.</p>
            <button
              onClick={() => setIsEditorOpen(true)}
              className="mt-4 text-blue-600 hover:text-blue-800 underline"
            >
              Add questions
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {templates.map((template) => (
              <div key={template.id} className="grid grid-cols-[240px_1fr] gap-4 items-start py-3 px-4 hover:bg-gray-50">
                <label
                  htmlFor={template.question_key}
                  className="text-sm font-medium text-gray-700 pt-2 flex items-center"
                >
                  {template.question_text}
                  {template.is_required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <div className="flex-1">
                  {renderQuestionInput(template)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isEditorOpen && user?.practice_id && (
        <TemplateEditorModal
          practiceId={user.practice_id}
          sectionName="plan"
          templates={templates}
          onClose={() => setIsEditorOpen(false)}
          onUpdate={() => {
            fetchTemplates();
            onUpdate();
          }}
        />
      )}

      {/* Floating save indicator */}
      {isSaving && (
        <div className="fixed bottom-6 right-6 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 animate-fade-in z-50">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          <span className="text-sm font-medium">Saving...</span>
        </div>
      )}
    </div>
  );
};

export default PlanSection;

